// Quick add popup for Pairs Well With (snippets/pdp-pairs-well-with.liquid).
// Content is sections/pdp-quick-add.liquid, fetched with the Section Rendering
// API so prices go through the same Wholesale Hub logic as the rest of the PDP.
(function () {
  if (window.pdpQuickAdd) return;
  window.pdpQuickAdd = true;

  const SECTION_ID = 'pdp-quick-add';
  let dialog;
  let trigger;
  let priceRequest = 0;

  function sectionUrl(url, variantId) {
    const target = new URL(url, window.location.origin);
    if (variantId) target.searchParams.set('variant', variantId);
    target.searchParams.set('section_id', SECTION_ID);
    return target.toString();
  }

  function fetchContent(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-add]'));
  }

  function getDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'quick-add-dialog';
    dialog.setAttribute('aria-labelledby', 'QuickAddTitle');
    dialog.innerHTML =
      '<button type="button" class="quick-add-dialog__close" data-quick-add-close aria-label="Close">' +
      '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">' +
      '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>' +
      '</button><div class="quick-add-dialog__body" data-quick-add-body></div>';
    document.body.appendChild(dialog);

    dialog.addEventListener('click', (event) => {
      // A click on the dialog element itself is a click on the backdrop.
      if (event.target === dialog || event.target.closest('[data-quick-add-close]')) {
        dialog.close();
        return;
      }

      const content = event.target.closest('[data-quick-add]');
      if (!content) return;

      const arrow = event.target.closest('[data-quick-add-arrow]');
      if (arrow) {
        // Wraps around: next on the last slide goes to the first, prev on the first to the last.
        const track = content.querySelector('[data-quick-add-gallery]');
        const slides = visibleSlides(content);
        const current = Math.round(track.scrollLeft / track.clientWidth);
        const next = (current + Number(arrow.dataset.quickAddArrow) + slides.length) % slides.length;
        track.scrollTo({ left: slides[next].offsetLeft, behavior: 'smooth' });
      } else if (event.target.closest('[data-quick-add-size-chart-open]')) {
        toggleSizeChart(content, true);
      } else if (event.target.closest('[data-quick-add-size-chart-close]')) {
        toggleSizeChart(content, false);
      }
    });
    // Esc closes the size chart first, then the popup.
    dialog.addEventListener('cancel', (event) => {
      const chart = dialog.querySelector('[data-quick-add-size-chart]:not([hidden])');
      if (!chart) return;
      event.preventDefault();
      toggleSizeChart(chart.closest('[data-quick-add]'), false);
    });
    dialog.addEventListener('close', () => {
      document.body.classList.remove('overflow-hidden');
      if (trigger) trigger.focus();
    });
    dialog.addEventListener('change', onOptionChange);
    dialog.addEventListener('submit', onSubmit);

    return dialog;
  }

  function open(link) {
    trigger = link;
    const modal = getDialog();
    const body = modal.querySelector('[data-quick-add-body]');
    body.innerHTML = '<p class="quick-add-dialog__loading" role="status">' + (window.accessibilityStrings?.loading || 'Loading…') + '</p>';
    document.body.classList.add('overflow-hidden');
    modal.showModal();

    fetchContent(sectionUrl(link.dataset.quickAddUrl))
      .then((content) => {
        if (!content) throw new Error('Missing quick add content');
        body.replaceChildren(document.adoptNode(content));
        content.variants = JSON.parse(content.querySelector('[data-quick-add-variants]').textContent);
        updateAvailability(content);
        setupGallery(content);
        content.querySelector('.quick-add__title')?.setAttribute('tabindex', '-1');
        content.querySelector('.quick-add__title')?.focus();
      })
      .catch(() => {
        // Fall back to the product page rather than leaving an empty popup.
        window.location.href = link.href;
      });
  }

  function fieldsets(content) {
    return Array.from(content.querySelectorAll('[data-option-index]'));
  }

  function selectedOptions(content) {
    return fieldsets(content).map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  function findVariant(content, options) {
    return content.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  function onOptionChange(event) {
    const content = event.target.closest('[data-quick-add]');
    if (!content || !event.target.matches('[data-option-index] input')) return;

    const changedIndex = Number(event.target.closest('[data-option-index]').dataset.optionIndex);
    const options = selectedOptions(content);
    let variant = findVariant(content, options);

    // The combination doesn't exist or is sold out (e.g. a colour without the
    // selected size in stock): keep the option just changed and move the rest
    // to its first available variant.
    if (!variant || !variant.available) {
      const fallback =
        content.variants.find((item) => item.available && item.options[changedIndex] === options[changedIndex]) ||
        variant;
      if (fallback && fallback !== variant) {
        variant = fallback;
        fieldsets(content).forEach((fieldset, index) => {
          const input = Array.from(fieldset.querySelectorAll('input')).find((item) => item.value === variant.options[index]);
          if (input) input.checked = true;
        });
      }
    }

    update(content, variant);
  }

  function updateAvailability(content) {
    const options = selectedOptions(content);
    fieldsets(content).forEach((fieldset, index) => {
      const isColor = fieldset.hasAttribute('data-option-color');
      fieldset.querySelectorAll('input').forEach((input) => {
        // Colours are available if any size is; other options depend on the rest of the selection.
        const available = content.variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === input.value &&
            (isColor || variant.options.every((value, i) => i === index || value === options[i]))
        );
        input.classList.toggle('disabled', !available);
      });
    });
  }

  function update(content, variant) {
    const form = content.querySelector('[data-quick-add-form]');
    const button = form.querySelector('[data-quick-add-submit]');
    const buttonText = button.querySelector('span');

    showError(content, false);
    updateAvailability(content);

    const color = content.querySelector('[data-option-color] input:checked');
    const colorLabel = content.querySelector('[data-quick-add-color]');
    if (color && colorLabel) colorLabel.textContent = color.value;

    if (!variant) {
      button.disabled = true;
      buttonText.textContent = window.variantStrings.unavailable;
      return;
    }

    form.querySelector('[name="id"]').value = variant.id;
    button.disabled = !variant.available;
    buttonText.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
    content.querySelector('[data-quick-add-details]').href = variant.url;

    filterGallery(content, variant.mediaColor);

    // Price comes from the server so Wholesale Hub pricing applies.
    const request = ++priceRequest;
    fetchContent(sectionUrl(content.dataset.productUrl, variant.id))
      .then((fresh) => {
        if (request !== priceRequest || !fresh) return;
        const price = fresh.querySelector('[data-quick-add-price]');
        if (price) content.querySelector('[data-quick-add-price]').innerHTML = price.innerHTML;
      })
      .catch(() => {});
  }

  function setupGallery(content) {
    const track = content.querySelector('[data-quick-add-gallery]');
    if (!track) return;
    track.addEventListener('scroll', () => updateDots(content), { passive: true });
    buildDots(content);
  }

  function visibleSlides(content) {
    return Array.from(content.querySelectorAll('.quick-add__slide:not([hidden])'));
  }

  // Mirrors the server-side filter in pdp-quick-add.liquid: untagged media shows
  // for every colour, and if the colour has no tagged media everything shows.
  function filterGallery(content, color) {
    const slides = Array.from(content.querySelectorAll('.quick-add__slide'));
    const hasMatch = Boolean(color) && slides.some((slide) => slide.dataset.mediaColor === color);
    slides.forEach((slide) => {
      const slideColor = slide.dataset.mediaColor;
      slide.hidden = hasMatch && slideColor !== '' && slideColor !== color;
    });

    const track = content.querySelector('[data-quick-add-gallery]');
    if (track) track.scrollLeft = 0;
    buildDots(content);
  }

  function buildDots(content) {
    const track = content.querySelector('[data-quick-add-gallery]');
    const dots = content.querySelector('[data-quick-add-dots]');
    const slides = visibleSlides(content);
    const multiple = slides.length > 1;

    content.querySelectorAll('[data-quick-add-arrow]').forEach((arrow) => (arrow.hidden = !multiple));
    if (!dots) return;
    dots.replaceChildren();
    dots.hidden = !multiple;
    if (!multiple) return;

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'quick-add__dot';
      dot.setAttribute('aria-label', `Show image ${index + 1} of ${slides.length}`);
      dot.addEventListener('click', () => track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' }));
      dots.appendChild(dot);
    });
    updateDots(content);
  }

  function updateDots(content) {
    const track = content.querySelector('[data-quick-add-gallery]');
    const dots = content.querySelectorAll('.quick-add__dot');
    if (!track || !dots.length) return;
    const active = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function toggleSizeChart(content, show) {
    const chart = content.querySelector('[data-quick-add-size-chart]');
    if (!chart) return;
    chart.hidden = !show;
    const focusTarget = show
      ? chart.querySelector('[data-quick-add-size-chart-close]')
      : content.querySelector('[data-quick-add-size-chart-open]');
    focusTarget?.focus();
  }

  function showError(content, message) {
    const error = content.querySelector('[data-quick-add-error]');
    if (!error) return;
    error.hidden = !message;
    error.textContent = message || '';
  }

  function onSubmit(event) {
    const form = event.target.closest('[data-quick-add-form]');
    if (!form) return;
    event.preventDefault();

    const content = form.closest('[data-quick-add]');
    const button = form.querySelector('[data-quick-add-submit]');
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-busy');

    // Mirrors product-form.js so the cart notification renders the same way.
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData(form);
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(trigger);
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'pdp-quick-add',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          showError(content, response.description || response.message);
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'pdp-quick-add',
          productVariantId: formData.get('id'),
          cartData: response,
        });
        dialog.close();
        cart.renderContents(response);
        cart.classList.remove('is-empty');
      })
      .catch(() => showError(content, window.cartStrings.error))
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-busy');
      });
  }

  // Delegated so it keeps working after the pairs are swapped on a variant change.
  // Modified clicks still open the product page as a normal link.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-quick-add-url]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    open(link);
  });
})();
